import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import { API_BASE_URL } from '../../constants/api';

export default function ApplyPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    services: [] as string[],
    experience: '',
    availability: '',
    hasEquipment: '',
    preferredAreas: [] as string[]
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const serviceOptions = [
    'Bathroom Cleaning Basic',
    'Bathroom Cleaning Intense',
    'Dusting',
    'Sofa Cleaning',
    'Carpet Cleaning',
    'Kitchen Utensil Cleaning',
    'Fan Cleaning',
    'Window Cleaning',
    'Cooking',
    'Water Filter',
  ];

  const areaOptions = [
    'Sitabuldi',
    'Sadar',
    'Dharampeth',
    'Civil Lines',
    'Hingna',
    'Wadi',
    'Kharbi',
    'Manish Nagar',
    'Trimurti Nagar',
    'Medical Square',
    'Seminary Hills',
    'Laxmi Nagar'
  ];

  const handleServiceChange = (service: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const handleAreaChange = (area: string) => {
    setFormData(prev => ({
      ...prev,
      preferredAreas: prev.preferredAreas.includes(area)
        ? prev.preferredAreas.filter(a => a !== area)
        : [...prev.preferredAreas, area]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.services.length === 0) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch(`${API_BASE_URL}/webforms/partner-application`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');

        // Reset form after successful submission
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            phone: '',
            services: [],
            experience: '',
            availability: '',
            hasEquipment: '',
            preferredAreas: []
          });
          setSubmitStatus('idle');
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white">
      <Navbar />

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-purple-600 to-purple-700 text-white relative overflow-hidden"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=professional%20service%20worker%20filling%20application%20form%20on%20tablet%2C%20modern%20office%20setting%2C%20purple%20and%20white%20theme%2C%20confident%20person%20applying%20for%20job%2C%20success%20and%20opportunity%20concept%2C%20clean%20professional%20photography%20with%20forms%20and%20documents&width=1200&height=500&seq=apply-hero&orientation=landscape')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}>
        <div className="absolute inset-0 bg-purple-600/80"></div>
        <div className="container mx-auto px-6 relative z-10 text-center">
          <h1 className="text-4xl font-bold mb-4">Apply to Join BNM</h1>
          <p className="text-xl max-w-2xl mx-auto">
            Start earning ₹25,000-₹50,000/month by providing trusted home services in Nagpur
          </p>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">Partner Application Form</h2>
                <p className="text-gray-600">Fill out the form below to begin your journey with BNM</p>
              </div>

              <form id="partner-application-form" onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Information */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      placeholder="Enter your phone number"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    placeholder="Enter your email address"
                  />
                </div>

                {/* Services Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-4">
                    Services You Can Provide * (Select all that apply)
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {serviceOptions.map((service) => (
                      <label key={service} className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          name="services"
                          value={service}
                          checked={formData.services.includes(service)}
                          onChange={() => handleServiceChange(service)}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">{service}</span>
                      </label>
                    ))}
                  </div>
                  {formData.services.length === 0 && submitStatus === 'error' && (
                    <p className="text-red-500 text-sm mt-1">Please select at least one service</p>
                  )}
                </div>

                {/* Experience Level */}
                <div>
                  <label htmlFor="experience" className="block text-sm font-semibold text-gray-700 mb-2">
                    Experience Level *
                  </label>
                  <div className="relative">
                    <select
                      id="experience"
                      name="experience"
                      required
                      value={formData.experience}
                      onChange={(e) => setFormData(prev => ({ ...prev, experience: e.target.value }))}
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm appearance-none bg-white"
                    >
                      <option value="">Select your experience level</option>
                      <option value="0-1 years">0-1 years (Beginner)</option>
                      <option value="1-3 years">1-3 years (Intermediate)</option>
                      <option value="3-5 years">3-5 years (Experienced)</option>
                      <option value="5+ years">5+ years (Expert)</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <i className="ri-arrow-down-s-line text-gray-400"></i>
                    </div>
                  </div>
                </div>

                {/* Availability */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-4">
                    Availability *
                  </label>
                  <div className="space-y-2">
                    {['Part-time (15-25 hours/week)', 'Full-time (35-45 hours/week)', 'Flexible (As needed)'].map((option) => (
                      <label key={option} className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="availability"
                          value={option}
                          checked={formData.availability === option}
                          onChange={(e) => setFormData(prev => ({ ...prev, availability: e.target.value }))}
                          className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Equipment */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-4">
                    Do you have your own equipment/tools? *
                  </label>
                  <div className="space-y-2">
                    {['Yes, I have all necessary equipment', 'Partially, I have some equipment', 'No, I need equipment provided'].map((option) => (
                      <label key={option} className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="hasEquipment"
                          value={option}
                          checked={formData.hasEquipment === option}
                          onChange={(e) => setFormData(prev => ({ ...prev, hasEquipment: e.target.value }))}
                          className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Preferred Areas */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-4">
                    Preferred Service Areas in Nagpur (Select all that apply)
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {areaOptions.map((area) => (
                      <label key={area} className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          name="preferredAreas"
                          value={area}
                          checked={formData.preferredAreas.includes(area)}
                          onChange={() => handleAreaChange(area)}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">{area}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-6">
                  {submitStatus === 'success' && (
                    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center">
                        <i className="ri-check-line text-green-600 mr-2"></i>
                        <p className="text-green-800 font-semibold">Application submitted successfully!</p>
                      </div>
                      <p className="text-green-700 text-sm mt-1">
                        We'll contact you within 2-3 business days to schedule your interview.
                      </p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center">
                        <i className="ri-error-warning-line text-red-600 mr-2"></i>
                        <p className="text-red-800 font-semibold">Please fill in all required fields</p>
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-purple-600 text-white py-4 rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Submitting Application...
                      </span>
                    ) : (
                      'Submit Application'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Next Steps */}
      <section className="py-16 bg-purple-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">What Happens Next?</h2>

            <div className="grid md:grid-cols-3 gap-8 mt-12">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-purple-600">1</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Application Review</h3>
                <p className="text-gray-600">We'll review your application within 24-48 hours</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-purple-600">2</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Interview & Verification</h3>
                <p className="text-gray-600">Phone interview and document verification process</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-purple-600">3</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Start Earning</h3>
                <p className="text-gray-600">Get onboarded and start accepting jobs immediately</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Support */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">Need Help with Your Application?</h2>
          <p className="text-xl text-gray-600 mb-8">
            Our support team is here to assist you
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a href="tel:93074 19845" className="flex items-center bg-purple-600 text-white px-6 py-3 rounded-full hover:bg-purple-700 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-phone-line mr-2"></i>
              <span className="font-semibold">Call: 93074 19845</span>
            </a>
            <a href="mailto:support@baainearme.com" className="flex items-center bg-gray-100 text-gray-800 px-6 py-3 rounded-full hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-mail-line mr-2"></i>
              <span className="font-semibold">Email Support</span>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              {/* BNM logo (SVG) — scalable, color via currentColor */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 420 160"
                className="h-10 text-white"   // change to text-purple-600 etc.
                role="img"
                aria-label="BNM · Baainearme"
              >
                <g fill="none" stroke="currentColor" strokeWidth="12" strokeLinecap="round">
                  {/* Left ring */}
                  <circle cx="50" cy="60" r="40" />
                  {/* Vertical divider */}
                  <line x1="110" y1="20" x2="110" y2="120" />
                </g>

                {/* Text block */}
                <g fill="currentColor">
                  {/* BNM */}
                  <text
                    x="140"
                    y="72"
                    fontFamily="Poppins, Inter, Arial, sans-serif"
                    fontWeight="800"
                    fontSize="56"
                  >
                    BNM
                  </text>

                  {/* Baainearme */}
                  <text
                    x="140"
                    y="120"
                    fontFamily="Poppins, Inter, Arial, sans-serif"
                    fontWeight="700"
                    fontSize="32"
                  >
                    Baainearme
                  </text>
                </g>
              </svg>

              <p className="text-gray-400">
                Join our partner network and start earning today
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">For Partners</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/apply" className="hover:text-white cursor-pointer">Apply Now</Link></li>
                <li><Link to="/partner" className="hover:text-white cursor-pointer">Partner Benefits</Link></li>
                <li><a href="#" className="hover:text-white cursor-pointer">Partner Support</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/customer" className="hover:text-white cursor-pointer">For Customers</Link></li>
                <li><Link to="/about" className="hover:text-white cursor-pointer">About Us</Link></li>
                <li><Link to="/faqs" className="hover:text-white cursor-pointer">FAQs</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Legal</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/partner-terms" className="hover:text-white cursor-pointer">Terms & Conditions</Link></li>
                <li><Link to="/partner-privacy" className="hover:text-white cursor-pointer">Privacy Policy</Link></li>
                <li><Link to="/refund-policy" className="hover:text-white cursor-pointer">Refund Policy</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Baainearme. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}